import React, { useState, useEffect, useRef } from 'react';
import './EmailTemplatesTab.css';

function EmailTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [htmlMode, setHtmlMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  const authHeaders = () => {
    const username = localStorage.getItem('admin_username');
    const password = localStorage.getItem('admin_password');
    return {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + btoa(`${username}:${password}`)
    };
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      // no-store: the API sends no cache headers, so without this the browser can serve a
      // stale list after a save (making a just-saved CC look like it vanished).
      const response = await fetch(`${apiEndpoint}/admin/email-templates`, { headers: authHeaders(), cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
        if (data.length && selectedKey == null) selectTemplate(data[0]);
      }
    } catch (err) {
      console.error('Error loading email templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = templates.find(t => t.key === selectedKey) || null;
  const dirty = selected ? (subject !== selected.subject || body !== selected.body || cc !== (selected.cc || '')) : false;

  // Push the current body HTML into the visual editor when the template or the edit mode changes
  // (NOT on every keystroke — that would fight the cursor while typing).
  useEffect(() => {
    if (!htmlMode && editorRef.current) editorRef.current.innerHTML = body;
  }, [selectedKey, htmlMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTemplate = (t) => {
    setSelectedKey(t.key);
    setSubject(t.subject);
    setBody(t.body);
    setCc(t.cc || '');
    setHtmlMode(false);
  };

  const handleSelect = (t) => {
    if (dirty && !window.confirm('Discard unsaved changes to this email?')) return;
    selectTemplate(t);
  };

  const syncFromEditor = () => {
    if (editorRef.current) setBody(editorRef.current.innerHTML);
  };

  const exec = (command, value = null) => {
    if (htmlMode) return;
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncFromEditor();
  };

  const addLink = () => {
    const url = window.prompt('Link URL:', 'https://');
    if (url) exec('createLink', url);
  };

  const insertField = (field) => {
    const token = `{{${field}}}`;
    if (htmlMode) {
      setBody(prev => prev + token);
      return;
    }
    editorRef.current?.focus();
    // Insert at the cursor; falls back to appending if the editor isn't focused.
    const ok = document.execCommand('insertText', false, token);
    if (!ok && editorRef.current) editorRef.current.innerHTML += token;
    syncFromEditor();
  };

  const handleSave = async () => {
    if (!selected || saving) return;
    if (!subject.trim()) { alert('Subject can’t be empty.'); return; }
    setSaving(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/email-templates/${selected.key}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ subject, body, cc })
      });
      if (response.ok) {
        setTemplates(prev => prev.map(t => t.key === selected.key ? { ...t, subject, body, cc } : t));
        alert('Email saved.');
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.error || 'Failed to save. Please try again.');
      }
    } catch (err) {
      console.error('Error saving email template:', err);
      alert('Error saving email. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !templates.length) {
    return <div className="email-templates"><div className="et-empty">Loading emails…</div></div>;
  }

  return (
    <div className="email-templates">
      <div className="et-list">
        {templates.map(t => (
          <button
            key={t.key}
            className={`et-list-item ${t.key === selectedKey ? 'active' : ''}`}
            onClick={() => handleSelect(t)}
          >
            <span className="et-list-title">{t.title}</span>
            <span className="et-list-recipient">{t.recipient}</span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="et-editor">
          <div className="et-editor-head">
            <h3>{selected.title}</h3>
            <p className="et-desc">{selected.description}</p>
          </div>

          <label className="et-label">Sends to</label>
          <div className="et-recipient">{selected.recipient} <span className="et-recipient-note">(automatic)</span></div>

          <label className="et-label">CC (optional: extra recipients, comma-separated)</label>
          <input
            className="et-subject"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="e.g. info@generationfaraday.com"
          />

          <label className="et-label">Subject</label>
          <input
            className="et-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <label className="et-label">Merge fields (click to insert)</label>
          <div className="et-fields">
            {selected.mergeFields.map(f => (
              <button key={f} className="et-field-chip" onClick={() => insertField(f)} title="Insert at the cursor">
                {`{{${f}}}`}
              </button>
            ))}
          </div>

          <div className="et-body-head">
            <label className="et-label" style={{ margin: 0 }}>
              {htmlMode ? 'Body (HTML)' : 'Body: this is how the email looks'}
            </label>
            <button
              className="et-mode-toggle"
              onClick={() => { if (htmlMode) { /* leaving HTML: editor re-syncs via effect */ } else { syncFromEditor(); } setHtmlMode(m => !m); }}
            >
              {htmlMode ? 'Visual editor' : 'Show HTML'}
            </button>
          </div>

          {!htmlMode && (
            <div className="et-toolbar">
              <button onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} title="Bold"><b>B</b></button>
              <button onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} title="Italic"><i>I</i></button>
              <button onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} title="Underline"><u>U</u></button>
              <button onMouseDown={(e) => { e.preventDefault(); addLink(); }} title="Add link">🔗</button>
              <button onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} title="Bulleted list">• List</button>
            </div>
          )}

          {htmlMode ? (
            <textarea
              className="et-body-html"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              spellCheck={false}
            />
          ) : (
            <div
              ref={editorRef}
              className="et-body-rich"
              contentEditable
              suppressContentEditableWarning
              onInput={syncFromEditor}
              onBlur={syncFromEditor}
            />
          )}

          <div className="et-actions">
            <button className="et-save" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving…' : dirty ? 'Save email' : 'Saved'}
            </button>
            {dirty && (
              <button className="et-revert" onClick={() => selectTemplate(selected)} disabled={saving}>
                Discard changes
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailTemplatesTab;
